/**
 * Recently Viewed Products renderer — Lithoforma
 *
 * Reads product IDs from the theme's own RecentlyViewed store
 * (assets/recently-viewed-products.js, written to on every product page
 * via snippets/scripts.liquid) and renders product cards into the
 * recently-viewed shell.
 *
 * Card markup mirrors the "You may also like" product card: a portrait
 * media box, then title, then price. Layout/spacing comes from
 * snippets/recently-viewed-products.liquid. Typography is delegated to the
 * theme's own preset classes so it always matches base.css:
 *
 *   title -> "paragraph"   price -> "h6"
 *
 * See the design contract comment in that snippet.
 *
 * MAX_DISPLAY matches that section's max_products setting (4).
 *
 * Design note: Shopify's public/unauthenticated storefront has no
 * endpoint that returns a product by numeric ID (that "ids" filter only
 * exists on the Admin API). Since the existing write-side stores numeric
 * IDs, this renderer fetches the small public catalog once via
 * /products.json (a real, documented, public endpoint) and matches
 * client-side, caching the result in sessionStorage for a few minutes so
 * repeat product-page views don't re-fetch the whole catalog. This is
 * fine for a catalog this size (dozens of products); if the catalog ever
 * grows past ~250 products, this needs pagination or a different lookup.
 */

import { RecentlyViewed } from '@theme/recently-viewed-products';

(function () {
  const MAX_DISPLAY = 4;
  const IMAGE_WIDTH = 500;
  const CATALOG_CACHE_KEY = 'lf-product-catalog-cache';
  const CATALOG_CACHE_TTL_MS = 10 * 60 * 1000; // 10 minutes

  function formatMoney(amount, currency) {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency: currency || 'EUR',
        minimumFractionDigits: 2
      }).format(amount);
    } catch {
      return amount.toFixed(2);
    }
  }

  function buildCard(product, currency, placeholderTemplate) {
    const card = document.createElement('a');
    card.href = '/products/' + product.handle;
    card.className = 'recently-viewed__item';

    // Media box — fixed portrait ratio, same as the recommendations cards.
    const media = document.createElement('div');
    media.className = 'recently-viewed__media';

    const imageSrc = product.images && product.images[0] ? product.images[0].src : null;
    if (imageSrc) {
      const img = document.createElement('img');
      img.src = imageSrc + (imageSrc.indexOf('?') === -1 ? '?' : '&') + 'width=' + IMAGE_WIDTH;
      img.alt = product.title;
      img.loading = 'lazy';
      media.appendChild(img);
    } else if (placeholderTemplate) {
      // Clone the site-wide placeholder component (icon + "Photo coming
      // soon") so imageless products look identical to every other grid.
      const fragment = placeholderTemplate.content.cloneNode(true);
      const placeholderEl = fragment.querySelector('.product-image-placeholder');
      if (placeholderEl) {
        placeholderEl.setAttribute('aria-label', product.title + ' — photo coming soon');
      }
      media.appendChild(fragment);
    }

    card.appendChild(media);

    // The `paragraph` class hands typography to the theme's base.css rule,
    // matching the recommendations card's title block preset.
    const title = document.createElement('p');
    title.className = 'recently-viewed__title paragraph';
    title.textContent = product.title;
    card.appendChild(title);

    const priceStr = product.variants && product.variants[0] ? product.variants[0].price : null;
    if (priceStr != null) {
      // Likewise `h6` matches that card's price block preset.
      const price = document.createElement('p');
      price.className = 'recently-viewed__price h6';
      price.textContent = formatMoney(parseFloat(priceStr), currency);
      card.appendChild(price);
    }

    return card;
  }

  async function getCatalog() {
    try {
      const cached = sessionStorage.getItem(CATALOG_CACHE_KEY);
      if (cached) {
        const parsed = JSON.parse(cached);
        if (Date.now() - parsed.timestamp < CATALOG_CACHE_TTL_MS) {
          return parsed.products;
        }
      }
    } catch {
      // sessionStorage unavailable or corrupt cache — fall through to fetch
    }

    try {
      const res = await fetch('/products.json?limit=250');
      if (!res.ok) return [];
      const data = await res.json();
      const products = data.products || [];

      try {
        sessionStorage.setItem(
          CATALOG_CACHE_KEY,
          JSON.stringify({ timestamp: Date.now(), products })
        );
      } catch {
        // Quota exceeded or storage disabled — non-fatal, just skip caching
      }

      return products;
    } catch {
      return [];
    }
  }

  async function init() {
    const shells = document.querySelectorAll('recently-viewed-products');
    if (!shells.length) return;

    let ids;
    try {
      ids = RecentlyViewed.getProducts();
    } catch {
      ids = [];
    }
    if (!ids.length) {
      shells.forEach((shell) => shell.setAttribute('data-empty', ''));
      return;
    }

    const catalog = await getCatalog();
    if (!catalog.length) {
      shells.forEach((shell) => shell.setAttribute('data-empty', ''));
      return;
    }

    const byId = new Map(catalog.map((p) => [String(p.id), p]));
    const currency = window.Shopify?.currency?.active
      || document.querySelector('meta[name="currency"]')?.content
      || 'EUR';

    shells.forEach(function (shell) {
      const currentId = shell.dataset.currentProductId;
      const grid = shell.querySelector('[data-recently-viewed-grid]');
      const placeholderTemplate = shell.querySelector('template[data-placeholder-template]');
      if (!grid) return;

      const filtered = ids
        .filter((id) => String(id) !== String(currentId))
        .map((id) => byId.get(String(id)))
        .filter(Boolean)
        .slice(0, MAX_DISPLAY);

      if (!filtered.length) {
        shell.setAttribute('data-empty', '');
        return;
      }

      filtered.forEach(function (product) {
        grid.appendChild(buildCard(product, currency, placeholderTemplate));
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
